import { useEffect, useRef, useState } from 'react';
import DesktopWindow from './DesktopWindow';
import { DEFAULT_LOCALE, LOCALES, isLocale, siteCopy } from './copy';
import './App.css';

const POCKETDEV_URL = 'https://pocketgamedeveloper.com/';
const DESIGN_WIDTH = 1280;
const STACK_BREAKPOINT = 760;
const publicUrl = process.env.PUBLIC_URL;

const WINDOW_LAYOUT = {
  home: { x: 140, y: 20, width: 640 },
  games: { x: 560, y: 28, width: 500 },
  about: { x: 150, y: 340, width: 720 },
  language: { x: 430, y: 72, width: 280 },
  destroyer: { x: 860, y: 16, width: 420 },
};

const DESK_ICONS = [
  { id: 'home', x: 16, y: 16 },
  { id: 'games', x: 16, y: 122 },
  { id: 'about', x: 16, y: 228 },
  { id: 'language', x: 16, y: 334 },
  { id: 'destroyer', x: 16, y: 440 },
];

const DEFAULT_ICON_POS = Object.fromEntries(
  DESK_ICONS.map((icon) => [icon.id, { x: icon.x, y: icon.y }]),
);

const LOCALE_STORAGE_KEY = 'straypixel-locale';

function readLocale() {
  try {
    const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(saved)) {
      return saved;
    }
  } catch (error) {
    return DEFAULT_LOCALE;
  }
  return DEFAULT_LOCALE;
}

function scaleLayout(surfaceWidth) {
  const pad = 16;
  const usable = Math.max(280, surfaceWidth - pad * 2);
  const scale = Math.min(1, usable / DESIGN_WIDTH);
  const next = {};

  Object.entries(WINDOW_LAYOUT).forEach(([id, rect]) => {
    const width = Math.max(220, Math.round(rect.width * scale));
    next[id] = {
      x: Math.min(
        Math.round(rect.x * scale) + pad,
        Math.max(pad, surfaceWidth - width - pad),
      ),
      y: Math.round(rect.y * scale) + pad,
      width,
    };
  });

  return next;
}

function App() {
  const surfaceRef = useRef(null);
  const fileRef = useRef(null);
  const [locale, setLocale] = useState(readLocale);
  const [openMenu, setOpenMenu] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [iconPos, setIconPos] = useState(DEFAULT_ICON_POS);
  const [hidden, setHidden] = useState({
    home: false,
    games: true,
    about: true,
    language: true,
    destroyer: true,
  });
  const [order, setOrder] = useState(['home']);
  const [raise, setRaise] = useState({ home: 0, games: 0, about: 0, language: 0, destroyer: 0 });
  const [surfaceWidth, setSurfaceWidth] = useState(DESIGN_WIDTH);
  const stacked = surfaceWidth < STACK_BREAKPOINT;
  const layout = stacked ? null : scaleLayout(surfaceWidth);
  const front = order.length ? order[order.length - 1] : null;
  const year = new Date().getFullYear();
  const copy = siteCopy[locale];
  const currentLocale = LOCALES.find((item) => item.code === locale) ?? LOCALES[0];

  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) {
      return undefined;
    }

    const update = () => setSurfaceWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch (error) {
      // Ignore storage failures; the in-memory locale still applies.
    }
  }, [locale]);

  useEffect(() => {
    if (!openMenu) {
      return undefined;
    }

    function onPointerDown(event) {
      if (!fileRef.current?.contains(event.target)) {
        setOpenMenu(null);
      }
    }

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        setOpenMenu(null);
      }
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenu]);

  function chooseLocale(next) {
    setLocale(next);
    setOpenMenu(null);
  }

  function focusWindow(id, restore = false) {
    setHidden((prev) => ({ ...prev, [id]: false }));
    setOrder((prev) => [...prev.filter((item) => item !== id), id]);
    if (restore) {
      setRaise((prev) => ({ ...prev, [id]: prev[id] + 1 }));
    }
    if (stacked) {
      window.requestAnimationFrame(() => {
        document.getElementById(`window-${id}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  }

  function zIndexFor(id) {
    return 10 + order.indexOf(id);
  }

  function openFromIcon(id) {
    setSelectedIcon(id);
    focusWindow(id, true);
  }

  function moveIcon(id, x, y) {
    const surface = surfaceRef.current;
    const maxX = Math.max(0, (surface?.clientWidth ?? 0) - 96);
    const maxY = Math.max(0, (surface?.clientHeight ?? 0) - 108);
    setIconPos((prev) => ({
      ...prev,
      [id]: {
        x: Math.min(maxX, Math.max(0, x)),
        y: Math.min(maxY, Math.max(0, y)),
      },
    }));
  }

  function openFrontWindow() {
    if (!front) {
      return;
    }
    setHidden((prev) => ({ ...prev, [front]: false }));
    setRaise((prev) => ({ ...prev, [front]: prev[front] + 1 }));
    setOpenMenu(null);
  }

  function closeFrontWindow() {
    if (!front) {
      return;
    }
    const id = front;
    setHidden((prev) => ({ ...prev, [id]: true }));
    setOrder((prev) => {
      const next = prev.filter((item) => item !== id);
      return next.length ? next : [id];
    });
    setOpenMenu(null);
  }

  return (
    <div className="desktop_shell">
      <header className="menu_bar">
        <div className="menu_bar_left">
          <img
            className="menu_brand"
            src={`${publicUrl}/straypixel.png`}
            alt="Stray Pixel Games"
          />
          {front ? <strong className="menu_window_title">{copy.nav[front]}</strong> : null}
          {front ? (
            <div className="menu_item" ref={fileRef}>
              <button
                type="button"
                className="menu_trigger"
                aria-expanded={openMenu === 'file'}
                aria-haspopup="menu"
                onClick={() => setOpenMenu((menu) => (menu === 'file' ? null : 'file'))}
              >
                {copy.file}
              </button>
              {openMenu === 'file' ? (
                <ul className="menu_dropdown" role="menu">
                  <li role="none">
                    <button type="button" role="menuitem" onClick={openFrontWindow}>
                      {copy.open}
                    </button>
                  </li>
                  <li role="none">
                    <button type="button" role="menuitem" onClick={closeFrontWindow}>
                      {copy.close}
                    </button>
                  </li>
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      <main
        ref={surfaceRef}
        className={`desktop_surface${stacked ? ' is-stacked' : ''}`}
        onPointerDown={(event) => {
          if (!event.target.closest('.desk_icon')) {
            setSelectedIcon(null);
          }
        }}
      >
        <h1 className="visually-hidden">Stray Pixel Games</h1>
        <div className={`desk_icon_rack${stacked ? ' is-stacked' : ''}`}>
          {DESK_ICONS.map((icon) => (
            <DeskIcon
              key={icon.id}
              id={icon.id}
              label={copy.nav[icon.id]}
              selected={selectedIcon === icon.id}
              stacked={stacked}
              position={iconPos[icon.id]}
              onSelect={setSelectedIcon}
              onOpen={openFromIcon}
              onMove={moveIcon}
            >
              <span className={`desk_icon_glyph${icon.id === 'about' ? ' desk_icon_glyph_pixel' : ''}${icon.id === 'home' ? ' desk_icon_glyph_smooth' : ''}`}>
                <DeskIconGlyph id={icon.id} flagIndex={currentLocale.flagIndex} />
              </span>
              <span className="desk_icon_label">{copy.nav[icon.id]}</span>
            </DeskIcon>
          ))}
        </div>
        {!hidden.home ? (
        <DesktopWindow
          id="home"
          title={copy.nav.home}
          zIndex={zIndexFor('home')}
          initial={WINDOW_LAYOUT.home}
          layout={layout?.home}
          stacked={stacked}
          raiseSignal={raise.home}
          onFocus={focusWindow}
          fitContent
          minimizeLabel={copy.minimize}
          maximizeLabel={copy.maximize}
          restoreLabel={copy.restore}
        >
          <div className="home_panel">
            <p className="home_tagline">{copy.tagline}</p>
            <img
              className="home_logo"
              src={`${publicUrl}/straypixel.png`}
              alt="Stray Pixel Games"
              draggable={false}
            />
          </div>
        </DesktopWindow>
        ) : null}

        {!hidden.games ? (
        <DesktopWindow
          id="games"
          title={copy.nav.games}
          zIndex={zIndexFor('games')}
          initial={WINDOW_LAYOUT.games}
          layout={layout?.games}
          stacked={stacked}
          raiseSignal={raise.games}
          onFocus={focusWindow}
          fitContent
          minimizeLabel={copy.minimize}
          maximizeLabel={copy.maximize}
          restoreLabel={copy.restore}
        >
          <GamesWindow copy={copy} />
        </DesktopWindow>
        ) : null}

        {!hidden.about ? (
        <DesktopWindow
          id="about"
          title={copy.nav.about}
          zIndex={zIndexFor('about')}
          initial={WINDOW_LAYOUT.about}
          layout={layout?.about}
          stacked={stacked}
          raiseSignal={raise.about}
          onFocus={focusWindow}
          fitContent
          minimizeLabel={copy.minimize}
          maximizeLabel={copy.maximize}
          restoreLabel={copy.restore}
        >
          <AboutWindow copy={copy} />
        </DesktopWindow>
        ) : null}

        {!hidden.language ? (
        <DesktopWindow
          id="language"
          title={copy.nav.language}
          zIndex={zIndexFor('language')}
          initial={WINDOW_LAYOUT.language}
          layout={layout?.language}
          stacked={stacked}
          raiseSignal={raise.language}
          onFocus={focusWindow}
          fitContent
          minimizeLabel={copy.minimize}
          maximizeLabel={copy.maximize}
          restoreLabel={copy.restore}
        >
          <LanguageWindow locale={locale} onChoose={chooseLocale} />
        </DesktopWindow>
        ) : null}

        {!hidden.destroyer ? (
        <DesktopWindow
          id="destroyer"
          title="Pocket Game Destroyer"
          zIndex={zIndexFor('destroyer')}
          initial={WINDOW_LAYOUT.destroyer}
          layout={layout?.destroyer}
          stacked={stacked}
          raiseSignal={raise.destroyer}
          onFocus={focusWindow}
          fitContent
          minimizeLabel={copy.minimize}
          maximizeLabel={copy.maximize}
          restoreLabel={copy.restore}
        >
          <DestroyerWindow copy={copy} />
        </DesktopWindow>
        ) : null}
      </main>

      <footer className="footer">
        <div className="footer_brand">
          <img
            className="footer_brand_mark"
            src={`${publicUrl}/icons/straypixel-mark.png`}
            alt=""
            width={48}
            height={48}
          />
          <p className="footer_copyright">© {year} Stray Pixel Games</p>
        </div>
      </footer>
    </div>
  );
}

function DeskIcon({
  id,
  label,
  selected,
  stacked,
  position,
  onSelect,
  onOpen,
  onMove,
  children,
}) {
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  function onPointerDown(event) {
    if (stacked || event.button !== 0) {
      return;
    }
    onSelect(id);
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
      moved: false,
    };
  }

  function onPointerMove(event) {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < 4) {
      return;
    }
    drag.moved = true;
    setDragging(true);
    onMove(id, drag.originX + dx, drag.originY + dy);
  }

  function finishDrag(event) {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    dragRef.current = null;
    setDragging(false);
    if (drag.moved) {
      event.currentTarget.dataset.dragged = '1';
    }
  }

  return (
    <button
      type="button"
      className={`desk_icon${selected ? ' is-selected' : ''}${dragging ? ' is-dragging' : ''}`}
      style={stacked ? undefined : { left: position.x, top: position.y }}
      aria-label={label}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onClick={(event) => {
        if (event.currentTarget.dataset.dragged === '1') {
          delete event.currentTarget.dataset.dragged;
          return;
        }
        onSelect(id);
      }}
      onDoubleClick={() => {
        if (dragging) {
          return;
        }
        onOpen(id);
      }}
    >
      {children}
    </button>
  );
}

function LanguageFlag({ index, large = false }) {
  return (
    <span
      className={`lang_flag${large ? ' lang_flag_large' : ''}`}
      data-flag={index}
      aria-hidden="true"
    />
  );
}

function DeskIconGlyph({ id, flagIndex }) {
  if (id === 'language') {
    return <LanguageFlag index={flagIndex} large />;
  }

  const src = {
    home: `${publicUrl}/icons/straypixel-mark.png`,
    games: `${publicUrl}/pocket-icon.png`,
    about: `${publicUrl}/rainbow-fish.png`,
    destroyer: `${publicUrl}/destroyer/Content/Sprites/red_button_idle.png`,
  }[id];

  return <img src={src} alt="" draggable={false} />;
}

function LanguageWindow({ locale, onChoose }) {
  return (
    <ul className="lang_window" role="listbox">
      {LOCALES.map((item) => {
        const selected = item.code === locale;
        return (
          <li key={item.code} role="none">
            <button
              type="button"
              className="menu_lang_option"
              role="option"
              aria-selected={selected}
              onClick={() => onChoose(item.code)}
            >
              <LanguageFlag index={item.flagIndex} />
              <span>{item.native}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function DestroyerWindow({ copy }) {
  const [started, setStarted] = useState(false);

  if (!started) {
    return (
      <button type="button" className="destroyer_start" onClick={() => setStarted(true)}>
        <img src={`${publicUrl}/destroyer/Content/Sprites/red_button_idle.png`} alt="" width={96} height={96} draggable={false} />
        <span>{copy.play}</span>
      </button>
    );
  }

  return (
    <iframe
      className="destroyer_frame"
      src={`${publicUrl}/destroyer/index.html`}
      title="Pocket Game Destroyer"
      allow="autoplay; fullscreen; gamepad"
    />
  );
}

function GamesWindow({ copy }) {
  return (
    <div className="games_panel">
      {copy.pocketDevDescription.split('\n\n').map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
      <a className="games_link" href={POCKETDEV_URL}>
        pocketgamedeveloper.com
      </a>
    </div>
  );
}

function AboutWindow({ copy }) {
  return (
    <div className="about_panel">
      <p className="about_story">{copy.story}</p>
      <ul className="about_team">
        <li className="about_card">
          <img
            src={`${publicUrl}/rainbow-fish.png`}
            alt=""
            width={16}
            height={16}
          />
          <h3>mushroomavocado</h3>
          <p>{copy.president}</p>
        </li>
        <li className="about_card">
          <img
            src={`${publicUrl}/cardanis.png`}
            alt=""
            width={16}
            height={16}
          />
          <h3>cardanis</h3>
          <p>{copy.otherPresident}</p>
        </li>
      </ul>
    </div>
  );
}

export default App;
